import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthTheme, getAuthStyles, injectAuthKeyframes } from './AuthTheme';

injectAuthKeyframes();

const PARTICLES = [
  { top: '10%', left: '8%', animationDelay: '0s' },
  { top: '20%', right: '10%', animationDelay: '1s' },
  { top: '60%', left: '5%', animationDelay: '2s' },
  { bottom: '20%', right: '8%', animationDelay: '0.5s' },
  { bottom: '10%', left: '20%', animationDelay: '1.5s' },
  { top: '45%', right: '5%', animationDelay: '2.5s' },
];

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [token, setToken] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { dark } = useAuthTheme();
  const s = getAuthStyles();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');
    if (!urlToken) { setTokenValid(false); setError('Invalid reset link. Please request a new one.'); return; }
    setToken(urlToken);
    fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/auth/verify-token/${urlToken}`)
      .then(r => r.json())
      .then(data => { if (!data.valid) { setTokenValid(false); setError('This reset link has expired or is invalid.'); } })
      .catch(() => { setTokenValid(false); setError('Failed to verify reset link.'); });
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setMessage('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) { setMessage('Password reset! Redirecting to login...'); setTimeout(() => navigate('/login'), 2500); }
      else setError(data.error || 'Failed to reset password');
    } catch { setError('Network error. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.orb1} /><div style={s.orb2} /><div style={s.orb3} />
      <div style={s.particles}>
        {PARTICLES.map((p, i) => <div key={i} style={{ ...s.particle, ...p }} />)}
      </div>

      <div style={s.wrap}>
        <div style={s.brand} onClick={() => navigate('/')}>
          <div style={s.logoRing}><span style={s.logoEmoji}>🎓</span><div style={s.logoGlow} /></div>
          <h1 style={s.brandName}>UniResolve</h1>
          <p style={s.brandSub}>Password Reset</p>
        </div>

        <div style={s.card}>
          <div style={s.cardTop}><div style={s.cardTopBadge}>{tokenValid ? 'Create New Password' : 'Invalid Link'}</div></div>
          <div style={s.cardBody}>
            {!tokenValid ? (
              <>
                <div style={s.err}><span>{error}</span></div>
                <button onClick={() => navigate('/forgot-password')} style={s.btn}>Request New Link</button>
                <div style={s.regRow}><span style={s.regLink} onClick={() => navigate('/login')}>Back to Login</span></div>
              </>
            ) : (
              <>
                <h2 style={s.heading}>Create New Password</h2>
                <p style={s.sub}>Enter your new password below</p>

                {error && <div style={s.err}><span>{error}</span><button type="button" onClick={() => setError('')} style={s.errX}>x</button></div>}
                {message && <div style={s.ok}><span>{message}</span></div>}

                <form onSubmit={handleSubmit}>
                  <div style={s.field}>
                    <label style={s.label}>New Password</label>
                    <div style={s.pwWrap}>
                      <input type={showPw ? 'text' : 'password'} value={password}
                        onChange={e => setPassword(e.target.value)} placeholder="Enter new password" style={s.input} required />
                      <button type="button" onClick={() => setShowPw(!showPw)} style={s.pwBtn}>{showPw ? 'Hide' : 'Show'}</button>
                    </div>
                    <span style={s.hint}>Minimum 6 characters</span>
                  </div>
                  <div style={s.field}>
                    <label style={s.label}>Confirm Password</label>
                    <div style={s.pwWrap}>
                      <input type={showCpw ? 'text' : 'password'} value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" style={s.input} required />
                      <button type="button" onClick={() => setShowCpw(!showCpw)} style={s.pwBtn}>{showCpw ? 'Hide' : 'Show'}</button>
                    </div>
                  </div>
                  <button type="submit" disabled={loading} style={{ ...s.btn, ...(loading ? s.btnOff : {}) }}>
                    {loading ? <span style={s.btnIn}><span style={s.spin} /> Resetting...</span> : 'Reset Password'}
                  </button>
                </form>
                <div style={s.regRow}>
                  <span style={s.regLink} onClick={() => navigate('/login')}>Back to Login</span>
                  <span style={{ color: 'rgba(128,128,128,0.5)', margin: '0 8px' }}>|</span>
                  <span style={s.regLink} onClick={() => navigate('/forgot-password')}>Request New Link</span>
                </div>
              </>
            )}
          </div>
        </div>
        <p style={s.footer}>2026 UniResolve</p>
      </div>
    </div>
  );
}
